import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customerDocumentsApi } from '../services/api';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ArrowLeft,
  PenTool,
  Type,
  Calendar,
  Mail,
  ZoomIn,
  ZoomOut,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Send,
  Loader,
  Download,
  Eye,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';
import './CustomerDocumentEditor.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

const DEFAULT_PAPER_SIZE = { width: 612, height: 792 };

// Same auto-scale logic as SigningPage
// Must match backend calcFontSize exactly (customerDocumentDownload.js)
const calcFontSize = (text, fieldWidth, fieldHeight, isSignature = false) => {
  if (isSignature) {
    const padX = 4, padY = 2;
    const availW = fieldWidth - padX * 2;
    const availH = fieldHeight - padY * 2;
    const fitsWidth = availW / (text.length * 0.45);
    const fitsHeight = availH * 0.9;
    return Math.max(10, Math.min(fitsWidth, fitsHeight, 60));
  }
  const availW = fieldWidth - 16;
  const availH = fieldHeight - 6;
  const fitsWidth = availW / (text.length * 0.5);
  const fitsHeight = availH * 0.7;
  return Math.max(8, Math.min(fitsWidth, fitsHeight, 18));
};

const SIGNATURE_FONTS = [
  { name: 'Dancing Script', css: "'Dancing Script', cursive" },
  { name: 'Great Vibes', css: "'Great Vibes', cursive" },
  { name: 'Pacifico', css: "'Pacifico', cursive" },
  { name: 'Sacramento', css: "'Sacramento', cursive" },
  { name: 'Caveat', css: "'Caveat', cursive" },
];

const SIGNATURE_FIELD_TYPES = [
  { type: 'signature', label: 'Signature', icon: PenTool, defaultWidth: 150, defaultHeight: 30 },
  { type: 'initials', label: 'Initials', icon: Type, defaultWidth: 60, defaultHeight: 28 },
  { type: 'date', label: 'Date', icon: Calendar, defaultWidth: 100, defaultHeight: 22 },
  { type: 'name', label: 'Printed Name', icon: Type, defaultWidth: 150, defaultHeight: 22 },
  { type: 'email', label: 'Email', icon: Mail, defaultWidth: 150, defaultHeight: 22 },
];

function CustomerDocumentEditor() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { selectedBlog } = useAuth();

  const customerDocumentId = location.state?.customerDocumentId || searchParams.get('docId');

  // Document data from backend
  const [customerDoc, setCustomerDoc] = useState(null);
  const [loadingDoc, setLoadingDoc] = useState(true);

  // Background
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [canvasSize, setCanvasSize] = useState(DEFAULT_PAPER_SIZE);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [pdfPageCount, setPdfPageCount] = useState(1);
  const [pdfRenderedPages, setPdfRenderedPages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);

  // Elements
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizeStart, setResizeStart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [placingField, setPlacingField] = useState(null); // field def awaiting placement click

  const nextId = useRef(1);
  const canvasRef = useRef(null);

  // Load document from backend
  useEffect(() => {
    if (!customerDocumentId) {
      navigate('/customer-documents');
      return;
    }
    loadDocument();
  }, [customerDocumentId]);

  const loadDocument = async () => {
    try {
      setLoadingDoc(true);
      const res = await customerDocumentsApi.get(customerDocumentId);
      const doc = res.data;
      setCustomerDoc(doc);

      // Load existing fields
      if (doc.fields && doc.fields.length > 0) {
        const mapped = doc.fields.map((f, idx) => ({
          id: idx + 1,
          dbId: f.id,
          type: 'signature',
          fieldType: f.field_type,
          page: f.page_number,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          label: f.label || f.field_type,
          signer: doc.customer_name,
          required: f.required,
          signOrder: f.sign_order,
          value: f.value,
          signedAt: f.signed_at
        }));
        setElements(mapped);
        nextId.current = mapped.length + 1;
      }

      // Load file as background
      const fileUrl = `/uploads/customer-documents/${doc.file_name}`;
      if (doc.file_type === 'pdf') {
        await loadPdfBackground(fileUrl, doc);
      } else {
        // Load image and detect dimensions
        const img = new Image();
        img.onload = () => {
          const maxW = 800;
          const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
          setCanvasSize({ width: img.naturalWidth * scale, height: img.naturalHeight * scale });
        };
        img.src = fileUrl;
        setBackgroundImage(fileUrl);
        setTotalPages(1);
      }
      // Auto-download if requested via URL param
      if (searchParams.get('download') === '1' && doc.status === 'completed') {
        const token = localStorage.getItem('token');
        fetch(`/api/customer-documents/doc/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.blob())
          .then(blob => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${doc.name} - Signed.pdf`;
            a.click();
            URL.revokeObjectURL(url);
          });
      }
    } catch (err) {
      toast.error('Failed to load document');
      navigate('/customer-documents');
    } finally {
      setLoadingDoc(false);
    }
  };

  const loadPdfBackground = async (url, doc) => {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      setPdfPageCount(pdf.numPages);
      setTotalPages(pdf.numPages);
      setCurrentPage(1);

      // Get actual page dimensions
      const firstPage = await pdf.getPage(1);
      const viewport = firstPage.getViewport({ scale: 1.0 });
      setCanvasSize({ width: viewport.width, height: viewport.height });

      // Pre-render first page before showing the document
      const canvas = window.document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const pixelRatio = 2;
      const scaledViewport = firstPage.getViewport({ scale: pixelRatio });
      canvas.width = viewport.width * pixelRatio;
      canvas.height = viewport.height * pixelRatio;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await firstPage.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setPdfRenderedPages(prev => ({ ...prev, 1: dataUrl }));

      // Set pdf document after first page is ready
      setPdfDocument(pdf);

      // Update page count in backend if different
      if (doc && doc.page_count !== pdf.numPages) {
        customerDocumentsApi.update(doc.id, { page_count: pdf.numPages }).catch(() => {});
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      toast.error('Failed to load PDF');
    }
  };

  // Render PDF page at native dimensions
  const renderPdfPage = useCallback(async (pageNum) => {
    if (!pdfDocument || pageNum < 1 || pageNum > pdfPageCount) return null;
    if (pdfRenderedPages[pageNum]) return pdfRenderedPages[pageNum];

    try {
      const page = await pdfDocument.getPage(pageNum);
      const canvas = window.document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const pixelRatio = 2;
      const scaledViewport = page.getViewport({ scale: pixelRatio });

      canvas.width = canvasSize.width * pixelRatio;
      canvas.height = canvasSize.height * pixelRatio;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setPdfRenderedPages(prev => ({ ...prev, [pageNum]: dataUrl }));
      return dataUrl;
    } catch (error) {
      console.error('Error rendering PDF page:', error);
      return null;
    }
  }, [pdfDocument, pdfPageCount, pdfRenderedPages]);

  useEffect(() => {
    if (pdfDocument && currentPage <= pdfPageCount) {
      renderPdfPage(currentPage);
    }
  }, [pdfDocument, currentPage, pdfPageCount, renderPdfPage]);

  const currentBackground = pdfDocument ? pdfRenderedPages[currentPage] : backgroundImage;

  // Select a field type for placement — user then clicks on the document to place it
  const handleSelectFieldForPlacement = (fieldDef) => {
    setPlacingField(fieldDef);
    setSelectedElement(null);
  };

  // Actually place the field where the user clicked on the canvas
  const handlePlaceField = (e) => {
    if (!placingField) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoomLevel / 100;
    const clickX = (e.clientX - rect.left) / scale;
    const clickY = (e.clientY - rect.top) / scale;

    // Center the field on the click point, clamped to canvas bounds
    const x = Math.max(0, Math.min(canvasSize.width - placingField.defaultWidth, clickX - placingField.defaultWidth / 2));
    const y = Math.max(0, Math.min(canvasSize.height - placingField.defaultHeight, clickY - placingField.defaultHeight / 2));

    const newElement = {
      id: nextId.current++,
      type: 'signature',
      fieldType: placingField.type,
      page: currentPage,
      x,
      y,
      width: placingField.defaultWidth,
      height: placingField.defaultHeight,
      label: placingField.label,
      signer: customerDoc?.customer_name || '',
      required: true,
      signOrder: elements.length
    };
    setElements(prev => [...prev, newElement]);
    setSelectedElement(newElement.id);
    setHasChanges(true);
    setPlacingField(null);
  };

  // Delete selected element
  const handleDeleteSelected = () => {
    if (selectedElement == null) return;
    setElements(prev => prev.filter(el => el.id !== selectedElement));
    setSelectedElement(null);
    setHasChanges(true);
  };

  // Canvas click — place field if in placement mode, otherwise deselect
  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current || e.target.classList.contains('grid-overlay')) {
      if (placingField) {
        handlePlaceField(e);
      } else {
        setSelectedElement(null);
      }
    }
  };

  // Element mouse down
  const handleElementMouseDown = (e, elementId) => {
    e.stopPropagation();
    e.preventDefault();

    // Don't allow dragging signed fields
    const element = elements.find(el => el.id === elementId);
    if (element?.signedAt) return;

    setSelectedElement(elementId);
    if (!element) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const scale = zoomLevel / 100;
    setDragOffset({
      x: (e.clientX - rect.left) / scale - element.x,
      y: (e.clientY - rect.top) / scale - element.y,
    });
    setIsDragging(true);
  };

  // Resize handle
  const handleResizeMouseDown = (e, elementId, handle) => {
    e.stopPropagation();
    e.preventDefault();

    const element = elements.find(el => el.id === elementId);
    if (element?.signedAt) return;

    setSelectedElement(elementId);
    if (!element) return;
    setIsResizing(true);
    setResizeHandle(handle);
    setResizeStart({
      mouseX: e.clientX, mouseY: e.clientY,
      x: element.x, y: element.y,
      width: element.width, height: element.height,
    });
  };

  // Mouse move
  const handleCanvasMouseMove = useCallback((e) => {
    if (isDragging && selectedElement != null) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = zoomLevel / 100;
      const newX = Math.max(0, Math.min(canvasSize.width - 50, (e.clientX - rect.left) / scale - dragOffset.x));
      const newY = Math.max(0, Math.min(canvasSize.height - 30, (e.clientY - rect.top) / scale - dragOffset.y));
      setElements(prev => prev.map(el =>
        el.id === selectedElement ? { ...el, x: newX, y: newY } : el
      ));
      setHasChanges(true);
    }

    if (isResizing && selectedElement != null && resizeStart) {
      const scale = zoomLevel / 100;
      const dx = (e.clientX - resizeStart.mouseX) / scale;
      const dy = (e.clientY - resizeStart.mouseY) / scale;

      setElements(prev => prev.map(el => {
        if (el.id !== selectedElement) return el;
        let { x, y, width, height } = resizeStart;
        switch (resizeHandle) {
          case 'se': width = Math.max(40, width + dx); height = Math.max(16, height + dy); break;
          case 'sw': width = Math.max(40, width - dx); height = Math.max(16, height + dy); x = resizeStart.x + (resizeStart.width - width); break;
          case 'ne': width = Math.max(40, width + dx); height = Math.max(16, height - dy); y = resizeStart.y + (resizeStart.height - height); break;
          case 'nw': width = Math.max(40, width - dx); height = Math.max(16, height - dy); x = resizeStart.x + (resizeStart.width - width); y = resizeStart.y + (resizeStart.height - height); break;
          case 'e': width = Math.max(40, width + dx); break;
          case 'w': width = Math.max(40, width - dx); x = resizeStart.x + (resizeStart.width - width); break;
          case 's': height = Math.max(16, height + dy); break;
          case 'n': height = Math.max(16, height - dy); y = resizeStart.y + (resizeStart.height - height); break;
        }
        return { ...el, x, y, width, height };
      }));
      setHasChanges(true);
    }
  }, [isDragging, isResizing, selectedElement, dragOffset, resizeStart, resizeHandle, zoomLevel]);

  const handleCanvasMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
    setResizeStart(null);
  }, []);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      setResizeStart(null);
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElement != null && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        handleDeleteSelected();
      }
      if (e.key === 'Escape') { setSelectedElement(null); setPlacingField(null); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElement]);

  // Save fields to backend
  const handleSave = async () => {
    try {
      setSaving(true);
      const fields = elements.map((el, idx) => ({
        field_type: el.fieldType,
        page_number: el.page,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        label: el.label,
        required: el.required !== false,
        sign_order: idx
      }));
      await customerDocumentsApi.saveFields(customerDoc.id, fields);
      setHasChanges(false);
      toast.success(`Saved ${fields.length} field${fields.length !== 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Auto-save fields, then send
  const handleSend = async () => {
    if (elements.length === 0) {
      toast.error('Place at least one signature field before sending');
      return;
    }
    try {
      // Auto-save first
      const fields = elements.map((el, idx) => ({
        field_type: el.fieldType,
        page_number: el.page,
        x: el.x,
        y: el.y,
        width: el.width,
        height: el.height,
        label: el.label,
        required: el.required !== false,
        sign_order: idx
      }));
      await customerDocumentsApi.saveFields(customerDoc.id, fields);
      setHasChanges(false);

      const res = await customerDocumentsApi.send(customerDoc.id);
      toast.success(res.data.email_sent ? 'Document sent!' : 'Document sent (share link manually)');
      if (res.data.signing_url) {
        navigator.clipboard.writeText(res.data.signing_url).catch(() => {});
      }
      navigate('/customer-documents');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send');
    }
  };

  // Zoom & page nav
  const handleZoomIn = () => setZoomLevel(z => Math.min(200, z + 25));
  const handleZoomOut = () => setZoomLevel(z => Math.max(50, z - 25));
  const handlePrevPage = () => setCurrentPage(p => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage(p => Math.min(totalPages, p + 1));

  const currentPageElements = elements.filter(el => el.page === currentPage);
  const isReadonly = customerDoc && customerDoc.status !== 'draft';

  if (loadingDoc) {
    return (
      <div className="cde" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader className="spin" size={32} />
      </div>
    );
  }

  if (!customerDoc) return null;

  return (
    <div className="cde">
      {/* Google Fonts for signed signature rendering */}
      <link
        href="https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Sacramento&display=swap"
        rel="stylesheet"
      />
      {/* Top bar */}
      <div className="cde-topbar">
        <div className="cde-topbar-left">
          <button className="cde-back-btn" onClick={() => navigate('/customer-documents')}>
            <ArrowLeft size={18} />
            Back
          </button>
          <div className="cde-topbar-title">
            <span className="cde-customer-name">{customerDoc.customer_name}</span>
            <span className="cde-file-name">{customerDoc.name}</span>
          </div>
        </div>

        <div className="cde-topbar-center">
          <div className="cde-zoom-controls">
            <button className="cde-tool-btn" onClick={handleZoomOut} title="Zoom out"><ZoomOut size={16} /></button>
            <span className="cde-zoom-label">{zoomLevel}%</span>
            <button className="cde-tool-btn" onClick={handleZoomIn} title="Zoom in"><ZoomIn size={16} /></button>
          </div>
          {totalPages > 1 && (
            <div className="cde-page-nav">
              <button className="cde-tool-btn" onClick={handlePrevPage} disabled={currentPage === 1}><ChevronLeft size={16} /></button>
              <span className="cde-page-label">Page {currentPage} / {totalPages}</span>
              <button className="cde-tool-btn" onClick={handleNextPage} disabled={currentPage === totalPages}><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        <div className="cde-topbar-right">
          {selectedElement != null && !isReadonly && (
            <button className="cde-tool-btn cde-delete-btn" onClick={handleDeleteSelected} title="Delete selected">
              <Trash2 size={16} />
            </button>
          )}
          {!isReadonly && (
            <>
              <button className="btn btn-secondary" onClick={async () => {
                if (hasChanges) await handleSave();
                setPreviewPage(1);
                setShowPreview(true);
              }} disabled={elements.length === 0}>
                <Eye size={16} />
                Preview
              </button>
              <button className="btn btn-primary" onClick={handleSend} disabled={elements.length === 0}>
                <Send size={16} />
                Send
              </button>
            </>
          )}
          {isReadonly && (
            <>
              {customerDoc.status === 'completed' && (
                <button className="btn btn-primary" onClick={() => {
                  const token = localStorage.getItem('token');
                  fetch(`/api/customer-documents/doc/${customerDoc.id}/download`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.blob())
                    .then(blob => {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${customerDoc.name} - Signed.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    });
                }}>
                  <Download size={16} />
                  Download
                </button>
              )}
              <span className="cde-readonly-badge">
                {customerDoc.status === 'completed' ? 'Completed' : customerDoc.status}
              </span>
            </>
          )}
        </div>
      </div>

      <div className="cde-body">
        {/* Left sidebar */}
        {!isReadonly && (
          <div className="cde-sidebar">
            <div className="cde-sidebar-header">
              <PenTool size={16} />
              <span>Signature Fields</span>
            </div>
            <p className="cde-sidebar-hint">
              {placingField
                ? `Click on the document to place the ${placingField.label} field.`
                : 'Select a field, then click on the document to place it.'}
            </p>
            <div className="cde-field-list">
              {SIGNATURE_FIELD_TYPES.map(fieldDef => {
                const Icon = fieldDef.icon;
                const isActive = placingField?.type === fieldDef.type;
                return (
                  <button
                    key={fieldDef.type}
                    className={`cde-field-btn ${isActive ? 'cde-field-btn-active' : ''}`}
                    onClick={() => isActive ? setPlacingField(null) : handleSelectFieldForPlacement(fieldDef)}
                  >
                    <Icon size={18} />
                    <span>{fieldDef.label}</span>
                  </button>
                );
              })}
            </div>

            {currentPageElements.length > 0 && (
              <div className="cde-placed-fields">
                <div className="cde-sidebar-header">
                  <span>Placed ({currentPageElements.length})</span>
                </div>
                {currentPageElements.map(el => (
                  <div
                    key={el.id}
                    className={`cde-placed-item ${selectedElement === el.id ? 'active' : ''}`}
                    onClick={() => setSelectedElement(el.id)}
                  >
                    <PenTool size={14} />
                    <span>{el.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Canvas area */}
        <div className="cde-canvas-area" onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp}>
          <div
            ref={canvasRef}
            className={`cde-canvas ${isDragging ? 'dragging' : ''} ${placingField ? 'placing' : ''}`}
            style={{
              width: canvasSize.width,
              height: canvasSize.height,
              transform: `scale(${zoomLevel / 100})`,
              transformOrigin: 'center top',
              backgroundImage: currentBackground ? `url(${currentBackground})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
            onClick={handleCanvasClick}
          >
            {currentPageElements.map(element => (
              <div
                key={element.id}
                className={`cde-element ${selectedElement === element.id ? 'selected' : ''} ${isDragging && selectedElement === element.id ? 'dragging' : ''} ${element.signedAt ? 'signed' : ''}`}
                style={{ left: element.x, top: element.y, width: element.width, height: element.height }}
                onMouseDown={(e) => handleElementMouseDown(e, element.id)}
                onClick={(e) => { e.stopPropagation(); setSelectedElement(element.id); }}
              >
                {element.signedAt && element.value ? (
                  <div className="cde-signed-value">
                    {(() => {
                      try {
                        const parsed = JSON.parse(element.value);
                        const fontSize = calcFontSize(parsed.text, element.width, element.height, element.fieldType === 'signature');
                        return <span style={{ fontFamily: `'${parsed.font}', cursive`, fontSize: `${fontSize}px`, lineHeight: 1, whiteSpace: 'nowrap' }}>{parsed.text}</span>;
                      } catch {
                        const fontSize = calcFontSize(element.value, element.width, element.height, false);
                        return <span style={{ fontSize: `${fontSize}px`, lineHeight: 1, whiteSpace: 'nowrap' }}>{element.value}</span>;
                      }
                    })()}
                  </div>
                ) : (
                  <span className="cde-sig-label">{element.label}</span>
                )}

                {selectedElement === element.id && !element.signedAt && !isReadonly && (
                  <>
                    <div className="resize-handle nw" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'nw')} />
                    <div className="resize-handle n" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'n')} />
                    <div className="resize-handle ne" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'ne')} />
                    <div className="resize-handle e" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'e')} />
                    <div className="resize-handle se" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'se')} />
                    <div className="resize-handle s" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 's')} />
                    <div className="resize-handle sw" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'sw')} />
                    <div className="resize-handle w" onMouseDown={(e) => handleResizeMouseDown(e, element.id, 'w')} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="cde-preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="cde-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="cde-preview-header">
              <h3>Preview - How the signed document will look</h3>
              <button className="cde-preview-close" onClick={() => setShowPreview(false)}><X size={20} /></button>
            </div>
            <div className="cde-preview-body">
              {totalPages > 1 && (
                <div className="cde-preview-page-nav">
                  <button onClick={() => setPreviewPage(p => Math.max(1, p - 1))} disabled={previewPage === 1}><ChevronLeft size={16} /></button>
                  <span>Page {previewPage} of {totalPages}</span>
                  <button onClick={() => setPreviewPage(p => Math.min(totalPages, p + 1))} disabled={previewPage === totalPages}><ChevronRight size={16} /></button>
                </div>
              )}
              <div
                className="cde-preview-canvas"
                style={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  backgroundImage: (pdfDocument ? pdfRenderedPages[previewPage] : backgroundImage) ? `url(${pdfDocument ? pdfRenderedPages[previewPage] : backgroundImage})` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  position: 'relative',
                }}
              >
                {elements.filter(el => el.page === previewPage).map(element => {
                  let text = '';
                  let fontStyle = {};
                  let isSignature = false;

                  if (element.fieldType === 'signature') {
                    text = customerDoc.customer_name || 'Signature';
                    const font = SIGNATURE_FONTS[0];
                    fontStyle = { fontFamily: font.css };
                    isSignature = true;
                  } else if (element.fieldType === 'initials') {
                    text = (customerDoc.customer_name || 'XX').split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase();
                    fontStyle = { fontFamily: SIGNATURE_FONTS[0].css };
                  } else if (element.fieldType === 'date') {
                    text = new Date().toLocaleDateString('en-US');
                  } else if (element.fieldType === 'name') {
                    text = customerDoc.customer_name || '';
                  } else if (element.fieldType === 'email') {
                    text = customerDoc.customer_email || '';
                  }

                  const fontSize = calcFontSize(text, element.width, element.height, isSignature);

                  return (
                    <div
                      key={element.id}
                      style={{
                        position: 'absolute',
                        left: element.x,
                        top: element.y,
                        width: element.width,
                        height: element.height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <span style={{
                        ...fontStyle,
                        fontSize: `${fontSize}px`,
                        color: '#1a1a2e',
                        whiteSpace: 'nowrap',
                        lineHeight: 1,
                      }}>
                        {text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="cde-preview-footer">
              <button className="btn btn-secondary" onClick={() => setShowPreview(false)}>Close & Adjust</button>
              <button className="btn btn-primary" onClick={() => { setShowPreview(false); handleSend(); }} disabled={elements.length === 0}>
                <Send size={16} /> Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDocumentEditor;
